import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sun, Moon, User, LogOut, Coins, Flame, Trophy, Gift, Sparkles, Target, ChevronDown, Bell, Ticket, Search } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { usePush } from "@/context/PushContext";
import { WeatherBadge } from "@/components/WeatherBadge";
import { CityInvite } from "@/components/CityInvite";

function NewsTodayMobile({ showVantaggi }) {
  return (
    <div data-testid="news-today-mobile" className="md:hidden border-b border-hair bg-surface">
      <div className="max-w-[1200px] mx-auto px-4 h-9 flex items-center gap-2">
        <WeatherBadge compact />
        {showVantaggi && (
          <Link to="/vantaggi" data-testid="header-vantaggi-mobile" className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
            <Gift size={14} /> Scopri i vantaggi
          </Link>
        )}
      </div>
    </div>
  );
}

function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex flex-col shrink-0 group leading-none">
      <span className="flex items-baseline gap-0.5">
        <span className="font-display text-2xl sm:text-[26px] font-bold tracking-tight text-onSurface group-hover:text-brand transition-colors">
          Readoo
        </span>
        <span className="w-2 h-2 rounded-full bg-brand mb-1" />
        <span data-testid="header-beta-badge" className="font-mono text-[10px] font-semibold uppercase tracking-wider text-brand ml-0.5">Beta</span>
      </span>
      <span data-testid="header-tagline" className="text-[11px] font-medium text-subtle leading-tight mt-0.5">
        L'informazione che ti premia
      </span>
    </Link>
  );
}

const NAV = [
  { to: "/premi", label: "Premi", icon: Gift },
];

export function Header() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { gamificationEnabled, loginEnabled, loaded: settingsLoaded } = useSettings();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  // La gamification è visibile solo se accesa E l'utente non è admin (gli admin non giocano).
  const gami = gamificationEnabled && !isAdmin;

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const doLogout = async () => { setMenuOpen(false); await logout(); navigate("/"); };
  const { state: pushState, promptSoftAsk, supported: pushSupp } = usePush();
  const showBell = user && gamificationEnabled && pushSupp && pushState !== "denied";

  return (
    <>
    <header className="sticky top-0 z-40 bg-surface/95 backdrop-blur-sm border-b border-hair">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
        <Logo />

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {gami && NAV.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              data-testid={`nav-link-${label.toLowerCase()}`}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-onSurfaceSecondary hover:text-brand hover:bg-surfaceSecondary transition-colors"
            >
              <Icon size={16} /> {label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {user && gami && (
            <div className="hidden sm:flex items-center gap-2">
              <span data-testid="streak-counter-pill" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surfaceSecondary border border-hair text-xs font-mono font-semibold text-brand">
                <Flame size={13} /> {user.streak || 0}
              </span>
              <Link to="/storico-punti" data-testid="user-points-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surfaceSecondary border border-hair text-xs font-mono font-semibold text-onSurface hover:border-brand/50 transition-colors">
                <Coins size={13} className="text-brand" /> {user.points || 0} Readoo
              </Link>
            </div>
          )}

          <WeatherBadge />

          <Link
            to="/cerca"
            data-testid="header-search-button"
            aria-label="Cerca su Readoo"
            title="Cerca"
            className="w-9 h-9 flex items-center justify-center rounded-full border border-hair bg-surfaceSecondary text-onSurfaceSecondary hover:text-brand hover:border-brand/50 transition-colors"
          >
            <Search size={17} />
          </Link>
          {showBell && (
            <button
              data-testid="header-notif-bell"
              onClick={promptSoftAsk}
              aria-label="Attiva notifiche"
              title={pushState === "on" ? "Notifiche attive" : "Attiva le notifiche"}
              className={`hidden sm:flex w-9 h-9 items-center justify-center rounded-full border transition-colors ${pushState === "on" ? "border-brand/50 bg-brandTertiary text-brand" : "border-hair bg-surfaceSecondary text-onSurfaceSecondary hover:text-brand hover:border-brand/50 animate-pulse"}`}
            >
              <Bell size={17} />
            </button>
          )}

          <button
            data-testid="theme-toggle-button"
            onClick={toggle}
            aria-label="Cambia tema"
            className="w-9 h-9 flex items-center justify-center rounded-full border border-hair bg-surfaceSecondary text-onSurfaceSecondary hover:text-brand hover:border-brand/50 transition-colors"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>

          {user ? (
            <div className="relative" ref={menuRef}>
              <button
                data-testid="user-menu-button"
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full border border-hair bg-surfaceSecondary hover:border-brand/50 transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-brandTertiary text-brand flex items-center justify-center font-serif font-bold text-sm">
                  {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                </span>
                <ChevronDown size={15} className="text-subtle" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-xl border border-hair bg-surfaceSecondary shadow-xl overflow-hidden animate-fade-up">
                  <div className="px-4 py-3 border-b border-hair">
                    <p className="text-sm font-semibold text-onSurface truncate">{user.name || "Lettore"}</p>
                    <p className="text-xs text-subtle truncate">{user.email}</p>
                  </div>
                  <MenuItem to="/profilo" icon={User} label="Profilo" onClick={() => setMenuOpen(false)} testid="menu-profilo" />
                  <MenuItem to="/il-mio-segno" icon={Sparkles} label="Il mio segno" onClick={() => setMenuOpen(false)} testid="menu-segno" />
                  <MenuItem to="/coupon" icon={Ticket} label="Coupon e sconti" onClick={() => setMenuOpen(false)} testid="menu-coupon" />
                  {gamificationEnabled && pushSupp && pushState !== "on" && pushState !== "denied" && (
                    <button
                      data-testid="menu-notif"
                      onClick={() => { setMenuOpen(false); promptSoftAsk(); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-onSurfaceSecondary hover:bg-surfaceTertiary hover:text-brand transition-colors sm:hidden"
                    >
                      <Bell size={16} /> Attiva notifiche
                    </button>
                  )}
                  {gami && (
                    <>
                      <MenuItem to="/missioni" icon={Target} label="Missioni" onClick={() => setMenuOpen(false)} testid="menu-missioni" />
                      <MenuItem to="/riscatti" icon={Gift} label="I miei riscatti" onClick={() => setMenuOpen(false)} testid="menu-riscatti" />
                    </>
                  )}
                  {isAdmin && (
                    <MenuItem to="/admin" icon={Trophy} label="Admin" onClick={() => setMenuOpen(false)} testid="menu-admin" />
                  )}
                  <button
                    data-testid="logout-button"
                    onClick={doLogout}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-bad hover:bg-surfaceTertiary transition-colors border-t border-hair"
                  >
                    <LogOut size={16} /> Esci
                  </button>
                </div>
              )}
            </div>
          ) : settingsLoaded && loginEnabled ? (
            <>
            <Link
              to="/vantaggi"
              data-testid="header-vantaggi-button"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand/40 text-brand text-sm font-semibold hover:bg-brandTertiary transition-colors"
            >
              <Gift size={15} /> Vantaggi
            </Link>
            <Link
              to="/login"
              data-testid="header-login-button"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors"
            >
              Accedi
            </Link>
            </>
          ) : null}
        </div>
      </div>
    </header>
    <NewsTodayMobile showVantaggi={!user && settingsLoaded && loginEnabled} />
    <CityInvite />
    </>
  );
}

function MenuItem({ to, icon: Icon, label, onClick, testid }) {
  return (
    <Link to={to} onClick={onClick} data-testid={testid} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-onSurfaceSecondary hover:bg-surfaceTertiary hover:text-brand transition-colors">
      <Icon size={16} /> {label}
    </Link>
  );
}
